import { Fragment, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  UserIcon,
  TagIcon,
  MailIcon,
  CalendarIcon,
  ClipboardIcon,
  CashIcon,
  UserGroupIcon,
  ClockIcon,
  MinusCircleIcon,
  DocumentTextIcon,
} from '@heroicons/react/outline';
import { useProviderServicesQuery } from '../../../generated/graphql';
import { AutomationExecution, AutomationRulePartial } from './types';
import { logger } from '../../../lib/logger';
import { getTagStyles } from '../../lib/colors';
import { getProviderSettingsTimezone, parseUtcIso } from '../../lib/time';
import { DEFAULT_TAG_COLOR } from './automationMetadata';
import { CONTACT_TYPE_LABELS } from '../PatientProfile/Account/Contacts/constants';
import { ContactType } from '../../../generated/graphql';
import TooltipLight from '../../components/TooltipLight';

type Props = {
  executions: AutomationExecution[];
  rules: AutomationRulePartial[];
};

const STATUS_CLASSNAME: Record<string, string> = {
  success: 'bg-green-25 text-green-150',
  partial_success: 'bg-yellow-25 text-yellow-150',
  failure: 'bg-red-25 text-red-150',
  skipped: 'bg-neutral-75 text-neutral-140',
};

const TRIGGER_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  client_created: UserIcon,
  client_tag_added: TagIcon,
  client_tag_removed: TagIcon,
  client_tag_changed: TagIcon,
  assessment_completed: ClipboardIcon,
  assessment_submitted: ClipboardIcon,
  appointment_scheduled: CalendarIcon,
  appointment_missed: CalendarIcon,
  session_completed: CalendarIcon,
  invoice_paid: CashIcon,
  practitioner_assigned: UserGroupIcon,
  service_booked: ClockIcon,
};

const ACTION_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  assign_tag: TagIcon,
  send_template: MailIcon,
  send_contact_form: DocumentTextIcon,
  send_notification: MailIcon,
  update_field: UserIcon,
  create_invoice: CashIcon,
};

const getRuleName = (
  execution: AutomationExecution,
  rules: AutomationRulePartial[],
) => {
  const matched = rules.find(
    (rule) =>
      rule.id === execution.automationRuleId ||
      rule.automationRuleId === execution.automationRuleId,
  );
  return matched?.name || execution.automationRuleId;
};

/**
 * Safely parses a JSON string, returning the raw value if parsing fails.
 * Logs errors to Sentry for monitoring malformed data from the backend.
 */
const parseJsonValue = (raw?: string | null) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    // Log but don't throw - malformed JSON is displayed as raw string to user
    logger.error('Failed to parse automation execution JSON data', {
      rawData: raw,
      error: error instanceof Error ? error : String(error),
    });
    return raw;
  }
};

/**
 * Extracts client name and email from event data using standard field names.
 * All event types now include client_first_name, client_last_name, client_email.
 */
const extractServiceTitleFromEventData = (
  data: Record<string, unknown>,
  providerServices?: Array<{ id: string; name: string }>,
): string | undefined => {
  const explicitName =
    (typeof data.service_name === 'string' && data.service_name.trim()) ||
    (typeof data.provider_service_name === 'string' &&
      data.provider_service_name.trim());
  if (explicitName) {
    return explicitName;
  }

  const serviceId = data.provider_service_id;
  if (typeof serviceId === 'string' && providerServices) {
    const match = providerServices.find((s) => s.id === serviceId);
    if (match?.name) {
      return match.name;
    }
  }

  if (
    typeof data.calendly_event_type_name === 'string' &&
    data.calendly_event_type_name.trim()
  ) {
    return data.calendly_event_type_name.trim();
  }

  return undefined;
};

const extractClientFromEventData = (
  data: Record<string, unknown>,
): { clientName?: string; clientEmail?: string } => {
  const firstName = data.client_first_name ?? data.first_name;
  const lastName = data.client_last_name ?? data.last_name;
  const email = data.client_email ?? data.email;
  const clientName =
    firstName && lastName
      ? `${firstName} ${lastName}`
      : typeof firstName === 'string'
      ? firstName
      : undefined;
  return {
    clientName: clientName ?? undefined,
    clientEmail: typeof email === 'string' ? email : undefined,
  };
};

type ExecutionTriggerDisplay = {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  details?: string;
  tagName?: string;
  tagColor?: string;
  clientName?: string;
  clientEmail?: string;
  serviceName?: string;
  isServiceBooked?: boolean;
};

function getServiceBookedTooltipText(
  serviceName?: string,
  clientName?: string,
  clientEmail?: string,
): string {
  let text = 'Service booked';
  if (serviceName) {
    text += `: ${serviceName}`;
  }
  if (clientName || clientEmail) {
    text += ` by ${clientName || clientEmail || ''}`;
    if (clientName && clientEmail) {
      text += ` (${clientEmail})`;
    }
  }
  return text;
}

function ServiceBookedTriggerContent({
  serviceName,
  clientName,
  clientEmail,
}: {
  serviceName?: string;
  clientName?: string;
  clientEmail?: string;
}) {
  const hasBooker = Boolean(clientName || clientEmail);

  return (
    <>
      <span>Service booked{serviceName ? ':' : ''}</span>
      {serviceName && (
        <>
          {' '}
          <strong>{serviceName}</strong>
        </>
      )}
      {hasBooker && (
        <>
          {' '}
          by{' '}
          {clientName ? (
            <strong>{clientName}</strong>
          ) : clientEmail ? (
            <strong>{clientEmail}</strong>
          ) : null}
          {clientName && clientEmail && (
            <span className="text-neutral-130"> ({clientEmail})</span>
          )}
        </>
      )}
    </>
  );
}

/**
 * Formats the trigger event into a human-readable description with icon.
 * Returns display fields for pill and tooltip rendering.
 */
const formatTriggerEvent = (
  execution: AutomationExecution,
  eventData: unknown,
  providerServices?: Array<{ id: string; name: string }>,
): ExecutionTriggerDisplay => {
  // Strip 'automation.' prefix if present (backend sends prefixed types)
  const triggerType = (execution.triggerEventType || '').replace(
    /^automation\./,
    '',
  );

  // Map trigger types to human-readable labels
  const triggerLabels: Record<string, string> = {
    client_created: 'Client created',
    client_tag_added: 'Tag applied',
    client_tag_removed: 'Tag removed',
    client_tag_changed: 'Tag applied',
    assessment_completed: 'Assessment completed',
    assessment_submitted: 'Assessment submitted',
    appointment_scheduled: 'Appointment scheduled',
    appointment_missed: 'Appointment missed',
    session_completed: 'Session completed',
    invoice_paid: 'Invoice paid',
    practitioner_assigned: 'Practitioner assigned',
    service_booked: 'Service booked',
  };

  const label = triggerLabels[triggerType] || triggerType.replace(/_/g, ' ');
  const Icon = TRIGGER_ICONS[triggerType] || UserIcon;

  if (!eventData || typeof eventData !== 'object') {
    return { label, icon: Icon };
  }

  const data = eventData as Record<string, unknown>;
  const { clientName, clientEmail } = extractClientFromEventData(data);

  // Tag events: extract tag info; label depends on action field
  if (
    triggerType === 'client_tag_added' ||
    triggerType === 'client_tag_removed' ||
    triggerType === 'client_tag_changed'
  ) {
    const tagName = data.tag_name;
    const tagColor = data.tag_color;
    let resolvedLabel = label;
    if (triggerType === 'client_tag_changed') {
      const action = String(data.action || '').toLowerCase();
      resolvedLabel = action === 'removed' ? 'Tag removed' : 'Tag applied';
    }
    return {
      label: resolvedLabel,
      icon: Icon,
      ...(tagName && typeof tagName === 'string' && { tagName }),
      ...(typeof tagColor === 'string' && { tagColor }),
      ...(clientName && { clientName }),
      ...(clientEmail && { clientEmail }),
      ...(clientName && clientEmail && { details: `(${clientEmail})` }),
    };
  }

  if (triggerType === 'service_booked') {
    const serviceName = extractServiceTitleFromEventData(
      data,
      providerServices,
    );
    return {
      label: 'Service booked',
      icon: Icon,
      isServiceBooked: true,
      serviceName,
      ...(clientName && { clientName }),
      ...(clientEmail && { clientEmail }),
    };
  }

  // Practitioner assigned: show practitioner name in details
  if (triggerType === 'practitioner_assigned') {
    const practitionerName =
      data.practitioner_name ||
      data.assigned_practitioner_name ||
      data.provider_name;
    if (practitionerName && clientName) {
      return {
        label,
        icon: Icon,
        clientName,
        clientEmail,
        details: `${practitionerName} assigned to ${clientName}`,
      };
    }
    if (practitionerName) {
      return { label, icon: Icon, details: `${practitionerName} assigned` };
    }
  }

  // All other events: show client name inline in label, email as details
  if (clientName) {
    return {
      label: `${label}: ${clientName}`,
      icon: Icon,
      clientName,
      clientEmail,
      ...(clientEmail && { details: `(${clientEmail})` }),
    };
  }

  return { label, icon: Icon };
};

/**
 * Formats action execution results into human-readable descriptions with icons.
 * Converts technical action results into business-friendly language.
 * Uses metadata from backend results to display action-specific details.
 */
const formatActionResults = (
  results: unknown,
  ruleActions: Array<{
    type?: string | null;
    params?: Array<{ name?: string; value?: string }>;
  }>,
  executionErrorMessage?: string | null,
): {
  description: string;
  details?: string;
  icon: React.ComponentType<{ className?: string }>;
  tagNames?: string[];
  tagColors?: string[];
  tagName?: string;
  tagColor?: string;
  templateName?: string;
  formName?: string;
  success: boolean;
}[] => {
  if (!Array.isArray(results)) {
    return [];
  }

  return results.map((result: unknown, index: number) => {
    const defaultIcon = MailIcon;

    // Get action type from rule definition by index (backend sends results in order)
    const ruleAction = ruleActions[index];
    const ruleActionType = ruleAction?.type;

    // Handle error case
    if (typeof result === 'object' && result !== null) {
      const r = result as Record<string, unknown>;
      const metadata = r.metadata as Record<string, unknown> | undefined;

      // Check for failure status
      if (
        r.status === 'error' ||
        r.status === 'failed' ||
        r.success === false
      ) {
        const actionType = metadata?.action_type || ruleActionType;
        const normalizedFailureType =
          typeof actionType === 'string'
            ? actionType.replace(/^automation\./, '')
            : undefined;
        const templateName = metadata?.template_name;
        const formName =
          normalizedFailureType === 'send_contact_form'
            ? metadata?.assessment_name
            : undefined;
        const tagName = metadata?.tag_name;
        const tagColor = metadata?.tag_color;

        // Build human-readable description
        let description = 'Action failed';
        if (actionType && typeof actionType === 'string') {
          const normalizedType = actionType.replace(/^automation\./, '');
          switch (normalizedType) {
            case 'send_template':
              description = 'Send Mail failed';
              break;
            case 'assign_tag':
              description = 'Failed to apply tag';
              break;
            case 'send_contact_form':
              description = 'Send form to Contact failed';
              break;
            case 'send_notification':
              description = 'Send notification failed';
              break;
            default:
              description = `${normalizedType} failed`;
          }
        }

        // Prefer per-action error message for specificity; fall back to top-level execution error
        const errorDetails = r.error_message
          ? String(r.error_message)
          : r.message
          ? String(r.message)
          : executionErrorMessage
          ? String(executionErrorMessage)
          : undefined;

        const result: {
          description: string;
          details?: string;
          icon: React.ComponentType<{ className?: string }>;
          success: false;
          tagName?: string;
          tagColor?: string;
          templateName?: string;
          formName?: string;
        } = {
          description,
          details: errorDetails,
          icon:
            normalizedFailureType === 'send_contact_form'
              ? DocumentTextIcon
              : defaultIcon,
          success: false,
        };

        if (formName && typeof formName === 'string') {
          result.formName = formName;
        } else if (templateName && typeof templateName === 'string') {
          result.templateName = templateName;
        }
        if (tagName && typeof tagName === 'string') {
          result.tagName = tagName;
        }
        if (tagColor && typeof tagColor === 'string') {
          result.tagColor = tagColor;
        }

        return result;
      }

      // Successful actions - use metadata for rich descriptions
      const resultActionType =
        metadata?.action_type || ruleActionType || r.type;

      if (!resultActionType || typeof resultActionType !== 'string') {
        return {
          description:
            r.message && typeof r.message === 'string'
              ? r.message
              : 'Action completed',
          icon: defaultIcon,
          success: true,
        };
      }

      // Strip 'automation.' prefix if present
      const normalizedActionType = resultActionType.replace(
        /^automation\./,
        '',
      );

      switch (normalizedActionType) {
        case 'assign_tag': {
          const tagNames = metadata?.tag_names;
          const tagColors = metadata?.tag_colors;

          if (tagNames && typeof tagNames === 'string') {
            const names = tagNames.split(',').map((n) => n.trim());
            const colors = Array.isArray(tagColors)
              ? tagColors
              : [DEFAULT_TAG_COLOR];

            return {
              description: 'Applied tag',
              icon: TagIcon,
              tagNames: names,
              tagColors: colors,
              success: true,
            };
          }
          return {
            description: 'Tag applied',
            icon: TagIcon,
            success: true,
          };
        }
        case 'send_template': {
          const templateName = metadata?.template_name;
          const recipientEmail = metadata?.recipient_email;
          if (templateName && typeof templateName === 'string') {
            return {
              description: 'Sent Mail',
              details: recipientEmail ? `(${recipientEmail})` : undefined,
              templateName,
              icon: MailIcon,
              success: true,
            };
          }
          return {
            description: 'Email sent',
            icon: MailIcon,
            success: true,
          };
        }
        case 'send_contact_form': {
          const formName = metadata?.assessment_name;
          const contactTypeRaw = metadata?.contact_type;
          const contactLabel =
            contactTypeRaw &&
            typeof contactTypeRaw === 'string' &&
            CONTACT_TYPE_LABELS[contactTypeRaw as ContactType]
              ? CONTACT_TYPE_LABELS[contactTypeRaw as ContactType]
              : typeof contactTypeRaw === 'string'
              ? contactTypeRaw
              : undefined;
          if (formName && typeof formName === 'string') {
            return {
              description: 'Sent form to Contact',
              details: contactLabel ? `(${contactLabel})` : undefined,
              formName,
              icon: DocumentTextIcon,
              success: true,
            };
          }
          return {
            description: 'Sent form to Contact',
            icon: DocumentTextIcon,
            success: true,
          };
        }
        case 'send_notification': {
          const recipient = metadata?.recipient;
          if (recipient === 'provider') {
            return {
              description: 'Sent notification to team',
              icon: MailIcon,
              success: true,
            };
          }
          return {
            description: 'Sent notification to client',
            icon: MailIcon,
            success: true,
          };
        }
        case 'update_field': {
          const field = metadata?.field;
          const newValue = metadata?.new_value;
          const fieldStr =
            typeof field === 'string' ? field.replace(/_/g, ' ') : '';
          const details =
            newValue !== undefined ? `To: ${String(newValue)}` : undefined;
          return {
            description: fieldStr ? `Updated ${fieldStr}` : 'Field updated',
            details,
            icon: UserIcon,
            success: true,
          };
        }
        case 'create_invoice': {
          const amount = metadata?.amount;
          if (typeof amount === 'number') {
            return {
              description: `Created invoice for $${amount.toFixed(2)}`,
              icon: CashIcon,
              success: true,
            };
          }
          return {
            description: 'Invoice created',
            icon: CashIcon,
            success: true,
          };
        }
        default:
          return {
            description:
              r.message && typeof r.message === 'string'
                ? r.message
                : `${normalizedActionType} completed`,
            icon: ACTION_ICONS[normalizedActionType] || defaultIcon,
            success: true,
          };
      }
    }

    return {
      description: 'Action completed',
      icon: defaultIcon,
      success: true,
    };
  });
};

/**
 * Determines the effective status for display purposes.
 * Returns 'partial_success' when status is 'success' but not all actions ran.
 */
function getDisplayStatus(execution: AutomationExecution): string {
  if (execution.status !== 'success') {
    return execution.status;
  }
  const n = execution.actionsExecuted ?? 0;
  const ok = execution.actionsSucceeded ?? 0;
  // If success overall but not all actions executed, show as partial
  if (n > 0 && ok < n) {
    return 'partial_success';
  }
  return execution.status;
}

/**
 * Renders the execution pills (trigger → actions) inline.
 * Unified component for both expanded and inline display.
 */
function ExecutionPills({
  execution,
  parsedEventData,
  parsedExecutionResults,
  ruleActions,
  providerServices,
}: {
  execution: AutomationExecution;
  parsedEventData: unknown;
  parsedExecutionResults: unknown;
  ruleActions: Array<{
    type?: string | null;
    params?: Array<{ name?: string; value?: string }>;
  }>;
  providerServices?: Array<{ id: string; name: string }>;
}) {
  const trigger = formatTriggerEvent(
    execution,
    parsedEventData,
    providerServices,
  );
  const TriggerIcon = trigger.icon;
  const actionResults = formatActionResults(
    parsedExecutionResults,
    ruleActions,
    execution.errorMessage,
  );

  const isTagEvent = trigger.tagName !== undefined;
  const isServiceBooked = trigger.isServiceBooked === true;

  // For tag events, fall back to recipient email from action results for old executions
  // that predate the client_email field being added to the event data
  const clientEmailFromActions =
    isTagEvent && !trigger.clientEmail
      ? actionResults
          .map((r) => r.details?.replace(/^\(|\)$/g, ''))
          .find(Boolean)
      : undefined;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {isTagEvent ? (
        <>
          {/* Client pill */}
          <span className="inline-flex items-center gap-[3px] rounded-full border border-neutral-100 bg-neutral-25 px-2 py-1 text-[11px] text-neutral-150">
            <UserIcon className="h-3.5 w-3.5 shrink-0" />
            <span>Client:</span>
            {trigger.clientName && <strong>{trigger.clientName}</strong>}
            {trigger.clientName && trigger.details && (
              <span className="text-neutral-130">{trigger.details}</span>
            )}
            {!trigger.clientName && clientEmailFromActions && (
              <span className="text-neutral-130">{clientEmailFromActions}</span>
            )}
          </span>
          {/* Tag pill */}
          <span className="inline-flex items-center gap-[3px] rounded-full border border-neutral-100 bg-neutral-25 px-2 py-1 text-[11px] text-neutral-150">
            <TriggerIcon className="h-3.5 w-3.5 shrink-0" />
            <span
              className="inline-flex rounded-full px-1.5 py-0.5 text-[10.5px] font-medium"
              style={getTagStyles(trigger.tagColor ?? DEFAULT_TAG_COLOR)}
            >
              {trigger.tagName}
            </span>
            {trigger.label}
          </span>
        </>
      ) : isServiceBooked ? (
        <span className="inline-flex items-center gap-[3px] rounded-full border border-primary-100 bg-primary-25 px-2 py-1 text-[11px] text-neutral-150">
          <TriggerIcon className="h-3.5 w-3.5 shrink-0" />
          <ServiceBookedTriggerContent
            serviceName={trigger.serviceName}
            clientName={trigger.clientName}
            clientEmail={trigger.clientEmail}
          />
        </span>
      ) : (
        /* Default single trigger pill */
        <span className="inline-flex items-center gap-[3px] rounded-full border border-primary-100 bg-primary-25 px-2 py-1 text-[11px] text-neutral-150">
          <TriggerIcon className="h-3.5 w-3.5 shrink-0" />
          {trigger.clientName ? (
            <>
              <span>{trigger.label.split(': ')[0]}:</span>
              <strong>{trigger.clientName}</strong>
              {trigger.details && (
                <span className="text-neutral-130">{trigger.details}</span>
              )}
            </>
          ) : trigger.details ? (
            <>
              <span>{trigger.label}</span>
              <span className="text-neutral-130">{trigger.details}</span>
            </>
          ) : (
            trigger.label
          )}
        </span>
      )}
      {/* Arrow */}
      <span className="text-neutral-120 text-[11px]">→</span>
      {/* Action pills */}
      {actionResults.length > 0 ? (
        actionResults.map((result, idx) => {
          const ActionIcon = result.icon;
          return (
            <span key={idx} className="inline-flex items-center gap-2">
              {idx > 0 && (
                <span className="text-neutral-120 text-[11px] font-medium">
                  and
                </span>
              )}
              {result.success ? (
                <>
                  {/* Main action pill */}
                  <span className="inline-flex items-center gap-[3px] rounded-full border border-green-100 bg-green-25 px-2 py-1 text-[11px] text-neutral-150">
                    <ActionIcon className="h-3.5 w-3.5 shrink-0 text-green-125" />
                    {result.formName ? (
                      <>
                        <span>Sent form to Contact</span>
                        <strong>{result.formName}</strong>
                        {result.details && (
                          <span className="text-neutral-130">
                            {result.details}
                          </span>
                        )}
                      </>
                    ) : result.templateName ? (
                      <>
                        <span>Sent Mail</span>
                        <strong>{result.templateName}</strong>
                        {result.details && (
                          <span className="text-neutral-130">
                            {result.details}
                          </span>
                        )}
                      </>
                    ) : result.tagNames && result.tagColors ? (
                      <>
                        {result.description}
                        {result.tagNames.map((tagName, tagIdx) => {
                          const tagColor =
                            result.tagColors?.[tagIdx] ?? DEFAULT_TAG_COLOR;
                          const tagStyles = getTagStyles(tagColor);
                          return (
                            <span
                              key={tagIdx}
                              className="inline-flex rounded-full px-1.5 py-0.5 text-[10.5px] font-medium"
                              style={tagStyles}
                            >
                              {tagName}
                            </span>
                          );
                        })}
                      </>
                    ) : (
                      <>
                        {result.description}
                        {result.details && (
                          <span className="text-neutral-130">
                            {result.details}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                </>
              ) : (
                <TooltipLight
                  content={
                    result.formName
                      ? `Failed to send form to Contact (${result.formName})${
                          result.details ? `: ${result.details}` : ''
                        }`
                      : result.templateName
                      ? `Failed to send Mail (${result.templateName})${
                          result.details ? `: ${result.details}` : ''
                        }`
                      : result.details
                      ? `${result.description}: ${result.details}`
                      : result.description
                  }
                >
                  <span className="inline-flex items-center gap-[3px] rounded-full border border-red-100 bg-red-25 px-2 py-1 text-[11px] text-neutral-150">
                    <ActionIcon className="h-3.5 w-3.5 shrink-0 text-red-125" />
                    {result.formName ? (
                      <>
                        <span>Failed to send form to Contact (</span>
                        <strong>{result.formName}</strong>
                        <span>)</span>
                        {result.details && (
                          <span className="text-neutral-130">
                            : {result.details}
                          </span>
                        )}
                      </>
                    ) : result.templateName ? (
                      <>
                        <span>Failed to send Mail (</span>
                        <strong>{result.templateName}</strong>
                        <span>)</span>
                        {result.details && (
                          <span className="text-neutral-130">
                            : {result.details}
                          </span>
                        )}
                      </>
                    ) : result.tagNames && result.tagColors ? (
                      <>
                        {result.description}
                        {result.tagNames.map((tagName, tagIdx) => {
                          const tagColor =
                            result.tagColors?.[tagIdx] ?? DEFAULT_TAG_COLOR;
                          const tagStyles = getTagStyles(tagColor);
                          return (
                            <span
                              key={tagIdx}
                              className="inline-flex rounded-full px-1.5 py-0.5 text-[10.5px] font-medium"
                              style={tagStyles}
                            >
                              {tagName}
                            </span>
                          );
                        })}
                      </>
                    ) : result.tagName && result.tagColor ? (
                      <>
                        {result.description}
                        <span
                          className="inline-flex rounded-full px-1.5 py-0.5 text-[10.5px] font-medium"
                          style={getTagStyles(result.tagColor)}
                        >
                          {result.tagName}
                        </span>
                        {result.details && (
                          <span className="text-neutral-130">
                            : {result.details}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        {result.description}
                        {result.details && (
                          <span className="text-neutral-130">
                            : {result.details}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                </TooltipLight>
              )}
            </span>
          );
        })
      ) : execution.status === 'skipped' ? (
        <span className="inline-flex items-center gap-[3px] rounded-full border border-neutral-100 bg-neutral-25 px-2 py-1 text-[11px] text-neutral-150">
          <MinusCircleIcon className="text-neutral-130 h-3.5 w-3.5 shrink-0" />
          Skipped: conditions not met
        </span>
      ) : (
        <span className="inline-flex items-center gap-[3px] rounded-full border border-neutral-100 bg-neutral-25 px-2 py-1 text-[11px] text-neutral-150">
          <MinusCircleIcon className="text-neutral-130 h-3.5 w-3.5 shrink-0" />
          No results
        </span>
      )}
    </div>
  );
}

/**
 * Props for mini icon pills shown in collapsed workflow state.
 */
interface MiniIconPillsProps {
  /** Trigger event info for the first pill */
  trigger: ExecutionTriggerDisplay;
  /** Array of action results to display as pills */
  actions: Array<{
    icon: React.ComponentType<{ className?: string }>;
    success: boolean;
    description: string;
    details?: string;
    templateName?: string;
    formName?: string;
    tagNames?: string[];
    tagColors?: string[];
  }>;
  /** Whether this is a no-actions execution */
  hasNoActions?: boolean;
  /** Execution status, used to distinguish skipped vs failed with no results */
  executionStatus?: string;
}

/**
 * Renders mini circular icon pills for collapsed workflow state.
 * Shows 28px colored circles with icons, tooltips on hover.
 */
function MiniIconPills({
  trigger,
  actions,
  hasNoActions,
  executionStatus,
}: MiniIconPillsProps) {
  const TriggerIcon = trigger.icon;
  const isTagEvent = trigger.tagName !== undefined;
  const isServiceBooked = trigger.isServiceBooked === true;

  if (hasNoActions) {
    const tooltip =
      executionStatus === 'skipped'
        ? 'Skipped: conditions not met'
        : 'No results';
    return (
      <span className="inline-flex items-center gap-1">
        <TooltipLight content={tooltip}>
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-neutral-100 bg-neutral-25">
            <MinusCircleIcon className="text-neutral-130 h-3.5 w-3.5" />
          </span>
        </TooltipLight>
      </span>
    );
  }

  // Build client tooltip for tag event client circle
  const getClientTooltip = () => {
    if (trigger.clientName && trigger.clientEmail) {
      return `Client: ${trigger.clientName} (${trigger.clientEmail})`;
    }
    if (trigger.clientName) return `Client: ${trigger.clientName}`;
    if (trigger.clientEmail) return `Client: ${trigger.clientEmail}`;
    return 'Client';
  };

  // Build trigger tooltip (tag name for tag events, or label + client name)
  const getTriggerTooltip = () => {
    if (isServiceBooked) {
      return getServiceBookedTooltipText(
        trigger.serviceName,
        trigger.clientName,
        trigger.clientEmail,
      );
    }
    if (isTagEvent) {
      return trigger.tagName
        ? `${trigger.label}: ${trigger.tagName}`
        : trigger.label;
    }
    if (trigger.clientName) {
      const base = trigger.label.split(': ')[0];
      return trigger.clientEmail
        ? `${base}: ${trigger.clientName} (${trigger.clientEmail})`
        : `${base}: ${trigger.clientName}`;
    }
    return trigger.label;
  };

  // Build detailed action tooltip content
  const getActionTooltip = (action: MiniIconPillsProps['actions'][number]) => {
    if (!action.success && action.formName) {
      return `Failed to send form to Contact (${action.formName})${
        action.details ? `: ${action.details}` : ''
      }`;
    }
    if (!action.success && action.templateName) {
      return `Failed to send Mail (${action.templateName})${
        action.details ? `: ${action.details}` : ''
      }`;
    }
    let content = action.description;
    if (action.formName) {
      content += ` (${action.formName})`;
    } else if (action.templateName) {
      content += ` (${action.templateName})`;
    }
    if (action.details) {
      content += ` ${action.details}`;
    }
    if (action.tagNames?.length) {
      content += ` (${action.tagNames.join(', ')})`;
    }
    return content;
  };

  return (
    <span className="inline-flex items-center gap-1">
      {/* For tag events: client circle first, then tag trigger circle */}
      {isTagEvent && (
        <TooltipLight content={getClientTooltip()}>
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-neutral-100 bg-neutral-25">
            <UserIcon className="text-neutral-130 h-3.5 w-3.5" />
          </span>
        </TooltipLight>
      )}

      {/* Trigger circle */}
      <TooltipLight content={getTriggerTooltip()}>
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-primary-100 bg-primary-25">
          <TriggerIcon className="h-3.5 w-3.5 text-green-125" />
        </span>
      </TooltipLight>

      {/* Action circles */}
      {actions.map((action, idx) => {
        const ActionIcon = action.icon;
        const pillColors = action.success
          ? 'border-green-100 bg-green-25'
          : 'border-red-100 bg-red-25';

        return (
          <span key={idx} className="inline-flex items-center gap-0.5">
            <span className="text-[10px] text-neutral-200">→</span>
            <TooltipLight content={getActionTooltip(action)}>
              <span
                className={`inline-flex h-7 w-7 items-center justify-center rounded-full border ${pillColors}`}
              >
                <ActionIcon
                  className={`h-3.5 w-3.5 ${
                    action.success ? 'text-green-125' : 'text-red-125'
                  }`}
                />
              </span>
            </TooltipLight>
          </span>
        );
      })}
    </span>
  );
}

const AUTOMATION_DATE_FORMAT = {
  month: 'short' as const,
  day: 'numeric' as const,
  year: 'numeric' as const,
};

const AUTOMATION_DATETIME_FORMAT = {
  weekday: 'short' as const,
  year: 'numeric' as const,
  month: 'short' as const,
  day: 'numeric' as const,
  hour: 'numeric' as const,
  minute: '2-digit' as const,
  second: '2-digit' as const,
  timeZoneName: 'short' as const,
};

function formatAutomationTimestamp(
  timestamp: string,
  timezone: string,
  includesTime: boolean,
): string {
  const dt = parseUtcIso(timestamp).setZone(timezone);
  if (!dt.isValid) {
    return timestamp;
  }
  return dt.toLocaleString(
    includesTime ? AUTOMATION_DATETIME_FORMAT : AUTOMATION_DATE_FORMAT,
  );
}

/**
 * Props for date display with tooltip.
 */
interface DateWithTooltipProps {
  /** ISO timestamp string */
  timestamp: string | null | undefined;
  /** IANA timezone string, e.g. 'America/Los_Angeles' */
  timezone: string;
}

/**
 * Displays short date format with full datetime tooltip on hover.
 * Matches Activities table styling.
 */
function DateWithTooltip({ timestamp, timezone }: DateWithTooltipProps) {
  if (!timestamp) {
    return <span className="text-neutral-130">-</span>;
  }

  const shortDate = formatAutomationTimestamp(timestamp, timezone, false);
  const fullDateTime = formatAutomationTimestamp(timestamp, timezone, true);

  return (
    <TooltipLight content={fullDateTime}>
      <span className="text-neutral-130">{shortDate}</span>
    </TooltipLight>
  );
}

export default function ExecutionsTable({ executions, rules }: Props) {
  const [expandedExecutionIds, setExpandedExecutionIds] = useState<Set<string>>(
    new Set(),
  );
  const { data: providerServicesData } = useProviderServicesQuery();
  const providerServices = providerServicesData?.providerServices ?? [];
  const { authedProviderUser } = useAuth();
  const providerTimezone = getProviderSettingsTimezone(
    (authedProviderUser?.provider as { timezone?: string } | undefined)
      ?.timezone,
  );

  const toggleRowExpansion = (executionId: string) => {
    setExpandedExecutionIds((prev) => {
      const next = new Set(prev);
      if (next.has(executionId)) {
        next.delete(executionId);
      } else {
        next.add(executionId);
      }
      return next;
    });
  };

  return (
    <div className="overflow-hidden rounded-md border border-neutral-100 bg-white">
      <div className="max-h-[calc(100vh-320px)] overflow-auto">
        <table className="min-w-full table-fixed">
          <thead className="sticky top-0 z-10 bg-neutral-25">
            <tr className="border-b border-neutral-100">
              <th className="text-xs text-neutral-140 w-24 px-3 py-2 text-left font-semibold">
                When
              </th>
              <th className="text-xs text-neutral-140 w-40 px-3 py-2 text-left font-semibold">
                Automation
              </th>
              <th className="text-xs text-neutral-140 w-20 px-3 py-2 text-left font-semibold">
                Status
              </th>
              <th className="text-xs text-neutral-140 w-48 px-3 py-2 text-left font-semibold">
                Workflow
              </th>
            </tr>
          </thead>
          <tbody>
            {executions.map((execution) => {
              const isExpanded = expandedExecutionIds.has(execution.id);
              const parsedEventData = parseJsonValue(execution.eventData);
              const parsedExecutionResults = parseJsonValue(
                execution.executionResults,
              );

              // Get the rule to access its action types
              const matchedRule = rules.find(
                (rule) =>
                  rule.id === execution.automationRuleId ||
                  rule.automationRuleId === execution.automationRuleId,
              );
              const ruleActions = matchedRule?.actions || [];
              const displayStatus = getDisplayStatus(execution);

              // Prepare data for MiniIconPills
              const trigger = formatTriggerEvent(
                execution,
                parsedEventData,
                providerServices,
              );
              const actionResults = formatActionResults(
                parsedExecutionResults,
                ruleActions,
                execution.errorMessage,
              );

              return (
                <Fragment key={execution.id}>
                  {/* Collapsed row (always visible) */}
                  <tr
                    onClick={() => toggleRowExpansion(execution.id)}
                    className={`cursor-pointer border-b border-neutral-75 hover:bg-neutral-25 ${
                      isExpanded ? 'bg-neutral-50' : ''
                    }`}
                  >
                    <td className="text-xs text-neutral-130 px-3 py-3">
                      <DateWithTooltip
                        timestamp={execution.startedAt}
                        timezone={providerTimezone}
                      />
                    </td>
                    <td className="text-xs text-neutral-130 px-3 py-3">
                      {getRuleName(execution, rules)}
                    </td>
                    <td className="text-xs px-3 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                          STATUS_CLASSNAME[displayStatus] ||
                          STATUS_CLASSNAME.skipped
                        }`}
                      >
                        {displayStatus === 'partial_success'
                          ? 'Partial'
                          : displayStatus.charAt(0).toUpperCase() +
                            displayStatus.slice(1)}
                      </span>
                    </td>
                    <td className="text-xs px-3 py-3">
                      <MiniIconPills
                        trigger={trigger}
                        actions={actionResults}
                        hasNoActions={actionResults.length === 0}
                        executionStatus={execution.status}
                      />
                    </td>
                  </tr>

                  {/* Expanded row (conditionally rendered) */}
                  {isExpanded && (
                    <tr className="border-b border-neutral-75 bg-neutral-50">
                      <td className="px-3 py-4" colSpan={4}>
                        <div className="overflow-hidden transition-all duration-200 ease-in-out">
                          {execution.startedAt && (
                            <p className="text-neutral-120 mb-2 text-[10px]">
                              {formatAutomationTimestamp(
                                execution.startedAt,
                                providerTimezone,
                                true,
                              )}
                            </p>
                          )}
                          <ExecutionPills
                            execution={execution}
                            parsedEventData={parsedEventData}
                            parsedExecutionResults={parsedExecutionResults}
                            ruleActions={ruleActions}
                            providerServices={providerServices}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
